import React, { CSSProperties } from "react";

import styles from "./sparkles.module.css";
import { useRandomInterval } from "./useRandomInterval";

const DEFAULT_MIN_SIZE = 10;
const DEFAULT_MAX_SIZE = 20;
const DEFAULT_MIN_DELAY = 50;
const DEFAULT_MAX_DELAY = 450;
const DEFAULT_MIN_OPACITY = 50;
const DEFAULT_MAX_OPACITY = 100;
const DEFAULT_COLOR = "#FFCF4A";

const random = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min)) + min;

const range = (start: number, end?: number, step = 1) => {
  let output: number[] = [];
  if (typeof end === "undefined") {
    end = start;
    start = 0;
  }

  for (let i = start; i < end; i += step) {
    output.push(i);
  }

  return output;
};

const generateSparkle = (
  color: string,
  minSize: number,
  maxSize: number,
  minOpacity: number,
  maxOpacity: number,
  speed: number,
  onTop: boolean,
) => {
  return {
    id: String(random(10000, 99999)),
    createdAt: Date.now(),
    color,
    speed,
    size: random(minSize, maxSize),
    opacity: random(minOpacity, maxOpacity),
    style: {
      top: random(0, 60) + "%",
      left: random(10, 90) + "%",
      zIndex: onTop ? 10 : undefined,
    },
  };
};

interface SparkleProps {
  size: number;
  color: string;
  style: CSSProperties;
  speed: number;
  opacity: number;
}

function Sparkle({ size, color, style, speed, opacity }: SparkleProps) {
  const path =
    "M26.5 25.5C19.0043 33.3697 0 34 0 34C0 34 19.1013 35.3684 26.5 43.5C33.234 50.901 34 68 34 68C34 68 36.9884 50.7065 44.5 43.5C51.6431 36.647 68 34 68 34C68 34 51.6947 32.0939 44.5 25.5C36.5605 18.2235 34 0 34 0C34 0 33.6591 17.9837 26.5 25.5Z";

  return (
    <div
      className={styles.sparklesSparkleWrapper}
      style={
        {
          ...style,
          "--animation-duration": `${speed}ms`,
        } as CSSProperties
      }
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 68 68"
        fill="none"
        className={styles.sparklesSvg}
        style={
          {
            opacity: opacity / 100,
            "--animation-duration": `${speed + 200}ms`,
          } as CSSProperties
        }
      >
        <path d={path} fill={color} />
      </svg>
    </div>
  );
}

interface SparklesProps {
  minSize?: number;
  maxSize?: number;
  minDelay?: number;
  maxDelay?: number;
  minOpacity?: number;
  maxOpacity?: number;
  color?: string;
  onTop?: boolean;
  children?: React.ReactNode;
  delegated?: React.HTMLAttributes<HTMLSpanElement>;
}

export function Sparkles({
  minSize = DEFAULT_MIN_SIZE,
  maxSize = DEFAULT_MAX_SIZE,
  minDelay = DEFAULT_MIN_DELAY,
  maxDelay = DEFAULT_MAX_DELAY,
  minOpacity = DEFAULT_MIN_OPACITY,
  maxOpacity = DEFAULT_MAX_OPACITY,
  color = DEFAULT_COLOR,
  onTop = false,
  children,
  ...delegated
}: SparklesProps) {
  const [sparkles, setSparkles] = React.useState(() => {
    return range(2).map(() =>
      generateSparkle(
        color,
        minSize,
        maxSize,
        minOpacity,
        maxOpacity,
        random(maxDelay - minDelay, maxDelay),
        onTop,
      ),
    );
  });

  const prefersReducedMotion = import.meta.env.SSR
    ? true
    : window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useRandomInterval(
    () => {
      const now = Date.now();
      const newSparkle = generateSparkle(
        color,
        minSize,
        maxSize,
        minOpacity,
        maxOpacity,
        random(maxDelay - minDelay, maxDelay),
        onTop,
      );

      const nextSparkles = sparkles
        .filter((sp) => {
          const delta = now - sp.createdAt;
          return delta < maxDelay + minDelay;
        })
        .concat([newSparkle]);

      setSparkles(nextSparkles);
    },
    prefersReducedMotion ? undefined : minDelay,
    prefersReducedMotion ? undefined : maxDelay,
  );

  return (
    <span className={styles.sparklesWrapper} {...delegated}>
      {sparkles.map((sparkle) => (
        <Sparkle
          key={sparkle.id}
          color={sparkle.color}
          size={sparkle.size}
          style={sparkle.style}
          speed={sparkle.speed}
          opacity={sparkle.opacity}
        />
      ))}
      <strong className={styles.sparklesChildWrapper}>{children}</strong>
    </span>
  );
}
